import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";

/**
 * Convert a category label to a URL-safe slug, e.g. "Hoefverzorging" → "hoefverzorging".
 */
export const categorySlug = (cat) =>
  (cat || "algemeen")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * BlogHubPage — overview with category filter chips.
 *
 * Props:
 *   items        — array of { slug, title, lead, eyebrow (=category), image }
 *   activeCategory — optional initial filter (used by /blog/categorie/{slug} routes)
 *   seo, path, eyebrow, title, intro, breadcrumbs — same as HubPage
 */
export const BlogHubPage = ({
  seo,
  path,
  eyebrow,
  title,
  intro,
  items = [],
  itemBasePath,
  breadcrumbs,
  activeCategory: initialCategory = null,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCat = searchParams.get("cat");
  const [active, setActive] = useState(
    initialCategory || (urlCat ? urlCat : "all")
  );

  const categories = useMemo(() => {
    const counts = {};
    items.forEach((it) => {
      counts[it.eyebrow] = (counts[it.eyebrow] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, slug: categorySlug(name), count }));
  }, [items]);

  const filtered = useMemo(() => {
    if (active === "all" || !active) return items;
    // active is a slug — match by slug
    return items.filter((it) => categorySlug(it.eyebrow) === active);
  }, [items, active]);

  const handleSelect = (slug) => {
    setActive(slug);
    if (slug === "all") {
      searchParams.delete("cat");
    } else {
      searchParams.set("cat", slug);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // For the generic /blog hub, prefer translated copy over passed props.
  const isGenericHub = !initialCategory;
  const displayTitle = isGenericHub ? t("blog.hubTitle", title) : title;
  const displayIntro = isGenericHub ? t("blog.hubIntro", intro) : intro;
  const displayEyebrow = isGenericHub ? t("nav.blog", eyebrow) : eyebrow;
  const displaySeoTitle = isGenericHub && seo?.title ? `${t("blog.hubTitle")} | EquiSight` : seo?.title;
  const displaySeoDesc = isGenericHub ? t("blog.hubIntro", seo?.description) : seo?.description;

  return (
    <main data-testid={`hub-${path.replace(/\//g, "")}`} className="bg-background">
      <Seo
        title={displaySeoTitle ?? seo?.title}
        description={displaySeoDesc ?? seo?.description}
        path={path}
        keywords={seo?.keywords}
        breadcrumbs={breadcrumbs}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          {displayEyebrow}
        </p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
          {displayTitle}
        </h1>
        {displayIntro && (
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
            {displayIntro}
          </p>
        )}

        {/* Category filter chips */}
        <div
          data-testid="blog-category-filter"
          className="mt-10 flex flex-wrap gap-2"
        >
          <button
            type="button"
            onClick={() => handleSelect("all")}
            data-testid="blog-cat-all"
            aria-pressed={active === "all"}
            className={
              "rounded-full border px-4 py-1.5 text-sm transition-colors " +
              (active === "all"
                ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-white"
                : "border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]")
            }
          >
            {t("blog.all")} <span className="opacity-60">({items.length})</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => handleSelect(c.slug)}
              data-testid={`blog-cat-${c.slug}`}
              aria-pressed={active === c.slug}
              className={
                "rounded-full border px-4 py-1.5 text-sm transition-colors " +
                (active === c.slug
                  ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-white"
                  : "border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]")
              }
            >
              {c.name} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>

        {/* Quick links to dedicated category landing pages (SEO) */}
        <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]">
          {t("blog.categoryLandingsIntro")}{" "}
          {categories.map((c, i) => (
            <React.Fragment key={c.slug}>
              <Link
                to={`/blog/categorie/${c.slug}`}
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                {c.name}
              </Link>
              {i < categories.length - 1 ? ", " : ""}
            </React.Fragment>
          ))}
        </p>

        <ul className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((it) => (
            <li key={it.slug}>
              <Link
                to={`${itemBasePath}/${it.slug}`}
                data-testid={`hub-item-${it.slug}`}
                className="group flex h-full flex-col justify-between overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] transition-all hover:-translate-y-1 hover:border-[hsl(var(--forest))]"
              >
                {it.image && (
                  <div className="aspect-[16/10] w-full overflow-hidden bg-[hsl(var(--surface-secondary))]">
                    <img
                      src={it.image}
                      alt={it.title}
                      loading="lazy"
                      className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                )}
                <div className="flex h-full flex-col justify-between p-7">
                  <div>
                    {it.eyebrow && (
                      <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                        {it.eyebrow}
                      </p>
                    )}
                    <h2 className="mt-3 font-display text-2xl font-medium leading-tight text-[hsl(var(--ink))]">
                      {it.title}
                    </h2>
                    {it.lead && (
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[hsl(var(--ink-soft))]">
                        {it.lead}
                      </p>
                    )}
                  </div>
                  <div className="mt-8 flex items-center justify-between text-sm text-[hsl(var(--forest))]">
                    <span>{t("common.readMore")}</span>
                    <ArrowUpRight
                      className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      strokeWidth={1.8}
                    />
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        {filtered.length === 0 && (
          <p
            data-testid="blog-empty-state"
            className="mt-12 text-[hsl(var(--ink-soft))]"
          >
            {t("blog.noArticles")}{" "}
            <button
              type="button"
              onClick={() => handleSelect("all")}
              className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
            >
              {t("blog.allBlogs")}
            </button>
            .
          </p>
        )}
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
};

export default BlogHubPage;
